/**
 * The conferencing port, in application vocabulary and with no vendor type on
 * it anywhere.
 *
 * The shape is unusual in one deliberate way: there is no `openRoom`. A room
 * exists only to be joined, so opening one folds into issuing a credential for
 * it. That is not tidiness — the vendor ends a room when its last attendee
 * leaves, so a handle held across an empty period is already dead, and a
 * separate `openRoom` would hand every caller that problem. Folded in, the
 * driver re-opens and returns the new handle, and no caller ever learns rooms
 * are reaped.
 *
 * `host` / `guest` are ours. A vendor's capability object is its spelling of
 * an access rule, and it stays inside the driver. The restriction is enforced
 * back-end, so a guest's screen share is refused however the client is
 * persuaded to ask for it.
 */

/** A room that exists right now. Its `id` is the vendor's, and opaque here. */
export interface RoomHandle {
  readonly id: string;
}

/**
 * Everything a browser needs to join, and the only thing that grants entry.
 * Never mint one before whoever guards the room has said yes.
 *
 * `payload` is `unknown` on purpose. The vendor's literal objects must
 * physically reach the browser, but nothing between here and there has any
 * business reading them, and `unknown` makes that a cast rather than a
 * temptation. Exactly one file performs that cast: the browser adapter.
 */
export type JoinCredential = { readonly payload: unknown };

declare const roomKeyBrand: unique symbol;

/**
 * What a room is keyed by: a scope and an id, joined with a colon —
 * `<scope>:<id>`.
 *
 * Opaque rather than a union of literal prefixes, because the scope words
 * belong to the callers, not here. A caller says `roomKey("visit", id)` and
 * this package stays free of the noun, which is the property its public
 * surface test enforces. Branded so an arbitrary string cannot be passed where
 * a key belongs, and so the separator rule below is the only way to build one.
 */
export type RoomKey = string & { readonly [roomKeyBrand]: "RoomKey" };

const SEPARATOR = ":";

/**
 * Build a key. The scope may not contain the separator: `("a:b", "c")` and
 * `("a", "b:c")` would otherwise render identically and silently share a room.
 */
export function roomKey(scope: string, id: string): RoomKey {
  if (scope.length === 0 || id.length === 0) throw new Error("A room key needs both a scope and an id");
  if (scope.includes(SEPARATOR)) throw new Error(`A room key scope may not contain "${SEPARATOR}": ${scope}`);
  return `${scope}${SEPARATOR}${id}` as RoomKey;
}

export interface ConferencingPort {
  /**
   * Open-or-reuse, then mint one credential for one person. `known` is
   * whatever the registry holds, or `undefined` for the first entrant; the
   * handle that comes back is authoritative and may differ from `known` when
   * the room had been reaped. Store it.
   */
  issueCredential(
    key: RoomKey,
    known: RoomHandle | undefined,
    displayName: string,
    role: "host" | "guest",
  ): Promise<{ room: RoomHandle; credential: JoinCredential }>;
  /**
   * Tear a room down. Already-gone is success, not an error.
   *
   * It does NOT forget the room in the registry — the caller owns that, and
   * should do it. Nothing breaks if it is skipped (the next issuance finds the
   * room gone and re-opens), but the stale handle costs a wasted round trip
   * and an avoidable re-open until its TTL passes.
   */
  closeRoom(room: RoomHandle): Promise<void>;
}

/**
 * Where a live room handle is shared between whoever issues credentials for
 * it. A cache, not state: an ephemeral vendor resource id is not a fact about
 * anything, and losing one costs a re-open.
 *
 * Async from day one. The in-memory implementation is a synchronous Map and
 * the deployed one is a network call, and discovering that difference later
 * means touching every caller.
 *
 * `ttl` is an instant, not a duration, because the right expiry is a property
 * of what the room is for — a room outliving its reason is how a stale handle
 * reaches the next person through it.
 */
export interface RoomRegistry {
  get(key: RoomKey): Promise<RoomHandle | undefined>;
  /**
   * Store `room` under `key` — but only if the key still holds what the caller
   * last read. Returns the handle that is actually held afterwards, which is
   * NOT `room` when somebody else got there first. Join what comes back.
   *
   * `replacing` is exactly what `get` returned before the room was opened, and
   * it is what makes this a compare-and-swap rather than a blind write. Both
   * halves are load-bearing:
   *
   *   - `undefined` means "I believed this key was free". It loses to a live
   *     holder, which is how two first entrants racing end up in one room.
   *   - A handle means "I believed this key held THAT, and it turned out to be
   *     dead". It displaces that handle and only that handle. A registry that
   *     refused here would keep serving the reaped room until its TTL passed,
   *     which is worse than no arbitration at all.
   *
   * A key whose holder has EXPIRED counts as free, on both sides of the
   * comparison — an expired handle never wins, exactly as `get` never returns
   * one. That is not defensiveness: the deployed registry's store deletes
   * expired records on its own schedule, hours late, so "expired" and "gone"
   * are genuinely different states there and only one of them is the store's
   * to report.
   *
   * The loser's own room is simply abandoned. It has no attendees, so the
   * vendor reaps it, and nobody was ever told it existed.
   */
  put(key: RoomKey, room: RoomHandle, ttl: Date, replacing: RoomHandle | undefined): Promise<RoomHandle>;
  /**
   * Drop whatever is under `key`. UNCONDITIONAL, unlike `put`, and still
   * uncalled — the meeting path never forgets a key, because issuing a
   * credential only ever opens or reuses, and a caller that loses the swap
   * abandons its own room rather than touching the winner's.
   *
   * ITS FIRST CALLER HAS A DECISION TO MAKE, and it is not the same decision
   * `put` faced. A `forget` racing a CAS win deletes the winner's handle, and
   * the next reader opens a third room while somebody is sitting in the second.
   * Whether that matters depends on WHY the key is being forgotten:
   *
   *   - Ending a visit: unconditional is arguably RIGHT. The visit is over, so
   *     every room under that key should be let go, including one a straggler
   *     opened a second ago. A compare would preserve exactly the room nobody
   *     should be in.
   *   - Tidying up after a specific handle went bad: a compare is needed, for
   *     the same reason `put` has one.
   *
   * Since the two want opposite behaviour, this is deliberately NOT given
   * `put`'s `replacing` argument on spec. Whoever writes the first caller picks
   * the semantics with a real case in front of them, and changes this signature
   * then if their case is the second one.
   */
  forget(key: RoomKey): Promise<void>;
}
